import React, { useEffect, useState, useCallback } from 'react';
import config from '../../../config/config'
import DataTable from 'react-data-table-component';
import Swal from 'sweetalert2'; // Import SweetAlert2

function UserList(props) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const result = await config.getAllUsers();
      setUsers(result.data || []);
      setError(null);
    } catch (err) {
      setUsers([]);
      setError(null); // Silently fail
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleEdit = async (userId) => {
    try {
      // SweetAlert2 Confirmation Dialog
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: 'You want to edit this user!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes!',
        cancelButtonText: 'No',
      });

      if (result.isConfirmed) {
        window.location.href = `editUser/${userId}`;

      }
    } catch (err) {
      // Error message if the API call fails
      Swal.fire('Error!', 'There was an issue editing the user.', 'error');
    }
  };

  const handleRemove = async (userId) => {
    try {
      // SweetAlert2 Confirmation Dialog
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: 'You will not be able to recover this user!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes!',
        cancelButtonText: 'No',
      });

      if (result.isConfirmed) {
        // If the user confirms the action, proceed with the API call
        await config.removeUser({ 'id': userId }); // Call the remove API
        fetchUsers(); // Re-fetch users after deletion

        // Success message after API call
        // Swal.fire('Deleted!', 'The user has been deleted successfully.', 'success');
      }
    } catch (err) {
      // Error message if the API call fails
      Swal.fire('Error!', 'There was an issue removing the user.', 'error');
    }
  };

  // const userData = users.map((user, index) => ({ ...user, id: index + 1 }));
  const userData = users.map((user, index) => ({ ...user, serialNo: index + 1 }));
  const columns = [
    { name: '#Id', selector: row => row._id, sortable: true, width: '60px' },
    {
      name: 'Image',
      width: '150px',
      cell: row => (
        <div style={{ display: 'flex', gap: '5px', alignItems: 'center', flexWrap: 'wrap', padding: '5px 0' }}>
          {row.image ? (
            row.image.split(',').map((img, idx) => (
              <img
                key={idx}
                src={`${config.BASE_URL}/uploads/users/${img}`}
                alt={`${row.name}-${idx}`}
                style={{ width: '40px', height: '40px', borderRadius: '4px', objectFit: 'cover', border: '1px solid #ddd' }}
                title={img}
              />
            ))
          ) : (
            <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#1572e8', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #ddd', color: '#fff', fontSize: '12px', fontWeight: '600' }}>
              {row.name ? row.name.charAt(0).toUpperCase() : 'U'}
            </div>
          )}
        </div>
      ),
    },
    { name: 'Name', selector: row => row.name, sortable: true, minWidth: '100px' },
    { name: 'Phone', selector: row => row.phone, minWidth: '100px' },
    { name: 'Email', selector: row => row.email, minWidth: '120px', omit: true },
    { name: 'Role', selector: row => row.userType == 1 ? 'Admin' : 'User', minWidth: '60px' },
    { name: 'Country', selector: row => row.country, minWidth: '80px', omit: true },
    { name: 'State', selector: row => row.state, minWidth: '80px', omit: true },
    { name: 'Pincode', selector: row => row.pincode, minWidth: '80px', omit: true },
    { name: 'Address', selector: row => row.address, minWidth: '120px', omit: true },
    {
      name: 'Actions',
      button: true,
      width: '150px',
      cell: row => (
        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
          <button
            onClick={() => handleEdit(row._id)}
            className="btn btn-warning btn-sm"
            style={{ padding: '0.25rem 0.5rem', fontSize: '11px' }}
          >
            Edit
          </button>
          <button
            onClick={() => handleRemove(row._id)}
            className="btn btn-danger btn-sm"
            style={{ padding: '0.25rem 0.5rem', fontSize: '11px' }}
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  const paginationOptions = {
    rowsPerPageText: 'Rows per page:',
    rangeSeparatorText: 'of',
    selectAllRowsItem: true,
    selectAllRowsItemText: 'All',
  };

  const customStyles = {
    headCells: {
      style: {
        paddingLeft: '8px',
        paddingRight: '8px',
        fontSize: '13px',
        fontWeight: '600',
      },
    },
    cells: {
      style: {
        paddingLeft: '8px',
        paddingRight: '8px',
        fontSize: '12px',
      },
    },
  };

  return (
    <div className="page-inner">
      <div className="page-header">
        <h3 className="fw-bold mb-3">Users</h3>
        <ul className="breadcrumbs mb-3">
          <li className="nav-home"><a href="#"><i className="fas fa-users"></i></a></li>
          <li className="separator"><i className="fa-solid fa-greater-than"></i></li>
          <li className="nav-item"><a href="#">Users</a></li>
        </ul>
      </div>

      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-header">
              <div className="card-title">
                <a href="/admin/user/addUpdateUser" className="btn btn-primary">
                  <i className="fas fa-plus"></i> Add User
                </a>
              </div>
            </div>

            <div className="card-body">
              {loading ? (
                <div>Loading users...</div>
              ) : (
                <DataTable
                  columns={columns}
                  data={userData}
                  pagination
                  paginationComponentOptions={paginationOptions}
                  paginationPerPage={10}
                  paginationRowsPerPageOptions={[5, 10, 15, 20, 50]}
                  striped
                  highlightOnHover
                  responsive
                  customStyles={customStyles}
                  noDataComponent={<div style={{padding: '20px'}}>No users available</div>}
                />
              )}
            </div>

            <div className="card-action"></div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserList;
